import {
  createContext,
  createEffect,
  createRoot,
  createSignal,
  getOwner,
  onCleanup,
  type Owner,
  type ParentProps,
  runWithOwner,
  useContext,
  type JSX,
  Suspense,
} from "solid-js"
import { Dialog as Kobalte } from "@kobalte/core/dialog"

type DialogElement = () => JSX.Element
type DialogOptions = {
  preventScroll?: boolean
}

type Active = {
  id: string
  node: JSX.Element
  dispose: () => void
  /** The opener's owner, which the dialog's own root lives under. */
  owner: Owner
  /** The dialog's own root; everything rendered inside the dialog descends from it. */
  root: Owner
  onClose?: () => void
  setClosing: (closing: boolean) => void
}

const Context = createContext<ReturnType<typeof init>>()

function init() {
  const [active, setActive] = createSignal<Active | undefined>()
  const timer = { current: undefined as ReturnType<typeof setTimeout> | undefined }
  const lock = { value: false }
  const escapeHandlers = new Set<() => boolean>()

  onCleanup(() => {
    if (timer.current === undefined) return
    clearTimeout(timer.current)
    timer.current = undefined
  })

  const close = () => {
    const current = active()
    if (!current || lock.value) return
    lock.value = true
    current.onClose?.()
    current.setClosing(true)

    const id = current.id
    if (timer.current !== undefined) {
      clearTimeout(timer.current)
      timer.current = undefined
    }

    timer.current = setTimeout(() => {
      timer.current = undefined
      current.dispose()
      if (active()?.id === id) setActive(undefined)
      lock.value = false
    }, 100)
  }

  createEffect(() => {
    if (!active()) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      const handled = [...escapeHandlers].reverse().some((handler) => handler())
      if (!handled) close()
      event.preventDefault()
      event.stopPropagation()
    }

    window.addEventListener("keydown", onKeyDown, true)
    onCleanup(() => window.removeEventListener("keydown", onKeyDown, true))
  })

  const show = (element: DialogElement, owner: Owner, onClose?: () => void, options?: DialogOptions) => {
    // Immediately dispose any existing dialog when showing a new one
    const current = active()
    if (current) {
      current.dispose()
      setActive(undefined)
    }

    if (timer.current !== undefined) {
      clearTimeout(timer.current)
      timer.current = undefined
    }
    lock.value = false

    const id = Math.random().toString(36).slice(2)
    let dispose: (() => void) | undefined
    let root: Owner | undefined
    let setClosing: ((closing: boolean) => void) | undefined

    const node = runWithOwner(owner, () =>
      createRoot((d: () => void) => {
        dispose = d
        root = getOwner() ?? undefined
        const [closing, setClosingSignal] = createSignal(false)
        setClosing = setClosingSignal
        return (
          <Kobalte
            modal
            preventScroll={options?.preventScroll}
            open={!closing()}
            onOpenChange={(open: boolean) => {
              if (open) return
              close()
            }}
          >
            <Kobalte.Portal>
              <Kobalte.Overlay data-component="dialog-overlay" onClick={close} />
              <Suspense fallback={<div class="dialog-loading" />}>
                {element()}
              </Suspense>
            </Kobalte.Portal>
          </Kobalte>
        )
      }),
    )

    if (!dispose || !setClosing || !root) return

    setActive({ id, node, dispose, owner, root, onClose, setClosing })
  }

  return {
    get active() {
      return active()
    },
    close,
    show,
    onEscape(handler: () => boolean) {
      escapeHandlers.add(handler)
      onCleanup(() => escapeHandlers.delete(handler))
    },
  }
}

export function DialogProvider(props: ParentProps) {
  const ctx = init()
  return (
    <Context.Provider value={ctx}>
      {props.children}
      <div data-component="dialog-stack">{ctx.active?.node}</div>
    </Context.Provider>
  )
}

function descendsFrom(owner: Owner | null, ancestor: Owner) {
  for (let node = owner; node; node = node.owner) {
    if (node === ancestor) return true
  }
  return false
}

export function useDialog() {
  const ctx = useContext(Context)
  const owner = getOwner()

  if (!owner) {
    throw new Error("useDialog must be used within a DialogProvider")
  }
  if (!ctx) {
    throw new Error("useDialog must be used within a DialogProvider")
  }

  return {
    get active() {
      return ctx.active
    },
    show(element: DialogElement, onClose?: () => void, options?: DialogOptions) {
      // Showing replaces the active dialog. A dialog opened from inside it
      // must outlive it, so it takes the active dialog's opener as owner; one
      // opened from anywhere else (a command chosen in a palette, say) keeps
      // the caller's owner and therefore the caller's contexts.
      const active = ctx.active
      const base = active && descendsFrom(owner, active.root) ? active.owner : owner
      ctx.show(element, base, onClose, options)
    },
    onEscape: ctx.onEscape,
    close() {
      ctx.close()
    },
  }
}
